/**
 * How the interviewer scores. Pure, so it is tested without rendering anything.
 *
 * Three rows, each READY / SHAKY / MISSING:
 *   - a skill question is READY when it was right first time AND the follow-up was right; SHAKY when
 *     one of those slipped or it took the hint; MISSING when even the retry was wrong.
 *   - the story is READY only for a real setting (a job), an "I" action and a result that can be named.
 *     A result the reader cannot name is MISSING, because that is the gap an externship fills.
 *
 * The verdict is deliberately coarse (three stamps, no numbers, no percentiles): we have no real
 * distribution to rank anyone against, and a false-precision score would be the first thing a sharp
 * reader stops trusting.
 */

export type Level = 'ready' | 'shaky' | 'missing';

export interface SkillOutcome {
  firstTry: boolean;
  /** Right on the follow-up (asked only after a right first answer). */
  followUp?: boolean;
  /** Right on the retry (asked only after a wrong first answer). */
  retry?: boolean;
}

export function skillLevel({ firstTry, followUp, retry }: SkillOutcome): Level {
  if (firstTry) {
    return followUp ? 'ready' : 'shaky';
  }
  return retry ? 'shaky' : 'missing';
}

export interface StoryPicks {
  actionWeight: number;
  resultIsGap: boolean;
  resultWeight: number;
  /** Null when the reader said they cannot think of one. */
  situationWeight: number | null;
}

export function storyLevel({ situationWeight, actionWeight, resultWeight, resultIsGap }: StoryPicks): Level {
  if (situationWeight === null || resultIsGap) {
    return 'missing';
  }
  return situationWeight >= 2 && actionWeight >= 1 && resultWeight >= 1 ? 'ready' : 'shaky';
}

/**
 * The interviewer's follow-up to the story, aimed at the WEAKEST part of it: "we" before a vague
 * result, a vague result before a low-stakes setting. Asking "what changed?" of someone who just said
 * they can name what changed reads like the interviewer was not listening.
 */
export function storyReaction({
  actionWeight,
  level,
  resultIsGap,
  resultWeight,
}: {
  actionWeight: number;
  level: Level;
  resultIsGap: boolean;
  resultWeight: number;
}): string {
  if (level === 'ready') {
    return 'That’s a solid answer. What was the number?';
  }
  if (actionWeight === 0) {
    return 'I heard a lot of “we”. What did you do, specifically?';
  }
  if (resultIsGap || resultWeight === 0) {
    return 'Good start. But what actually changed because of it?';
  }
  return 'Okay. Who was it for, and what was at stake?';
}

export type DecisionKind = 'advance' | 'borderline' | 'not-yet';

const POINTS: Record<Level, number> = { missing: 0, ready: 1, shaky: 0.5 };

export function decide(levels: readonly Level[]): DecisionKind {
  const points = levels.reduce((sum, level) => sum + POINTS[level], 0);
  if (points >= 2.5) {
    return 'advance';
  }
  return points >= 1.5 ? 'borderline' : 'not-yet';
}

export const DECISION_LABEL: Record<DecisionKind, string> = {
  advance: 'Advance to round 2',
  borderline: 'Borderline',
  'not-yet': 'Not yet · one fix away',
};
